import {
  Button,
  Drawer,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box } from "@mui/system";
import { bool, func, object } from "prop-types";
import { useSnackbar } from "notistack";
import { useAddModule, useUpdateModule } from "@/hooks";
import { useTextDomainContext } from "@/app/providers";
import { KeyTextField } from "@/components";

type AddModuleProps = {
  open?: boolean;
  handleClose: (open: boolean) => void;
  row: {
    code?: string | null;
    name?: string | null;
    desc?: string | null;
  };
};

export const AddModule = (props: AddModuleProps) => {
  const { enqueueSnackbar } = useSnackbar();
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  const propsDefault = {
    open: false,
  };

  const [inputName, setInputName] = useState("");
  const [inputCode, setInputCode] = useState("");
  const [inputDesc, setInputDesc] = useState("");
  // const [saveMode, setSaveMode] = useState("add");

  // Use props.open directly instead of maintaining state
  const open = props.open ?? propsDefault.open;

  useEffect(() => {
    if (props.row.code) {
      // Use setTimeout to defer state updates and avoid cascading renders
      setTimeout(() => {
        setInputName(props.row.name);
        setInputCode(props.row.code);
        setInputDesc(props.row.desc);
      }, 0);
    } else {
      setTimeout(() => {
        setInputName("");
        setInputCode("");
        setInputDesc("");
      }, 0);
    }
  }, [props.row]);

  const handleClose = () => {
    props.handleClose(false);
  };

  const resetForm = useCallback(() => {
    setInputCode("");
    setInputName("");
    setInputDesc("");
  }, []);

  const { addModule, loading: updatingAdd } = useAddModule(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The new module has been added"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const { updateModule, loading: updatingEdit } = useUpdateModule(
    () => {
      resetForm();
      handleClose();
      enqueueSnackbar(gettext("The module has been updated"), {
        variant: "success",
      });
    },
    (error) => {
      if (error === "409") {
        // setEmailError(true);
      } else if (error === "410") {
        enqueueSnackbar(
          gettext("You are not authorized to perform this action"),
          {
            variant: "error",
          },
        );
      } else {
        enqueueSnackbar(gettext("Error: Something went wrong!"), {
          variant: "error",
        });
      }
    },
  );

  const submitHandler = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const variables = {
      code: inputCode,
      name: inputName,
      desc: inputDesc,
    };
    if (props.row.code) {
      updateModule({
        variables: variables,
        refetchQueries: ["getAllModules"],
      }).then();
    } else {
      addModule({
        variables: variables,
        refetchQueries: ["getAllModules"],
      }).then();
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputName(event.target.value);
  };

  const handleCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputCode(event.target.value);
  };

  const handleDescChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputDesc(event.target.value);
  };

  return (
    <Drawer
      anchor={"right"}
      open={open}
      onClose={handleClose}
      sx={{
        zIndex: (theme) => theme.zIndex.appBar + 1,
      }}
      slotProps={{
        paper: {
          sx: {
            padding: 4,
            maxWidth: 450,
          },
        },
      }}
    >
      <Box
        sx={{
          width: "auto",
        }}
      >
        <form onSubmit={submitHandler}>
          <Stack
            spacing={4}
            sx={{
              height: "100%",
            }}
          >
            <Typography sx={{ fontSize: 22, fontWeight: 700 }}>
              {!props.row.code && gettext("Add New Module")}
              {props.row.code && gettext("Edit Module")}
            </Typography>

            <Box
              sx={{
                height: "100%",
              }}
            >
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    type="text"
                    label={gettext("Name")}
                    placeholder={gettext("Module name")}
                    size="small"
                    required
                    fullWidth
                    value={inputName}
                    onChange={handleNameChange}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <KeyTextField
                    type="text"
                    label={gettext("Code")}
                    placeholder={gettext(
                      "Module code, Alpha numeric and dash only",
                    )}
                    size="small"
                    required
                    fullWidth
                    value={inputCode}
                    onChange={handleCodeChange}
                    disabled={!!props.row.code}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <TextField
                    multiline
                    maxRows={4}
                    label={gettext("Description")}
                    placeholder={gettext("Description")}
                    fullWidth
                    value={inputDesc}
                    onChange={handleDescChange}
                  />
                </Grid>
              </Grid>
            </Box>

            <Paper elevation={0}>
              <Grid container spacing={4} sx={{ width: "100%" }}>
                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleClose()}
                  >
                    {gettext("Cancel")}
                  </Button>
                </Grid>

                <Grid size={{ xs: 6 }} sx={{ textAlign: "center" }}>
                  <Button
                    variant="contained"
                    loading={updatingAdd || updatingEdit}
                    fullWidth
                    type="submit"
                  >
                    {gettext("Save")}
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </Stack>
        </form>
      </Box>
    </Drawer>
  );
};

AddModule.propTypes = {
  open: bool,
  handleClose: func,
  row: object,
};
